import React, { useState } from "react"
import { Container, Header } from "./Shared.styled"
import { ReactComponent as ChevronDown } from "../../../../images/icons/chevron-down.svg"
import { ReactComponent as ChevronUp } from "../../../../images/icons/chevron-up.svg"
import { Order } from "lib/types/generated/graphql-types"
import { Compensation } from "../../../../classes/order/Compensation"
import Compensations from "./Compensations"
import Money from "../../../Money"
import DineroFactory from "dinero.js"
import { Refund } from "../../../../classes/order/Refund"
import RefundInfo from "./RefundInfo"
import { Delivery } from "../../../../classes/order/Delivery"
import DeliveryInfo from "./DeliveryInfo"
import { Release } from "../../../../classes/order/Release"
import ReleaseInfo from "./ReleaseInfo"
import { Cancellation } from "../../../../classes/order/Cancellation"
import CancellationInfo from "./CancellationInfo"

type Props = {
  order: Order
  orderLineId: string
  salePriceAmount: number
}

const Operations = ({ order, orderLineId, salePriceAmount }: Props) => {
  const [expanded, setExpanded] = useState(false)
  const compensationsClass = new Compensation(order.Compensations)
  const refund = new Refund(order.Refunds)
  const delivery = new Delivery(order.Deliveries)
  const release = new Release(order.Releases)
  const cancellation = new Cancellation(order.Cancellations)

  const orderLineCompensations = compensationsClass.getCompensationsOnOrderLine(orderLineId)
  const compensatedAmount = compensationsClass.orderLineTotal(orderLineId)

  const orderLineRefund = refund.getRefundedOrderLine(orderLineId)
  const orderLineRefundedAmount = refund.orderLineRefundedAmount(orderLineId)

  const orderLineDelivered = delivery.orderLineDelivered(orderLineId)
  const orderLineReleased = release.orderLineReleased(orderLineId)
  const orderCancelled = cancellation.orderCancelled(order.id)

  if (
    orderLineCompensations.length === 0 &&
    orderLineRefundedAmount === 0 &&
    !orderLineDelivered &&
    !orderLineReleased &&
    !orderCancelled
  )
    return null

  const getOperationAmount = () => {
    if (orderLineReleased) {
      return (
        orderLineReleased?.orderLines?.find((ol) => ol.orderLineId === orderLineId)?.totalAmount ??
        0
      )
    }
    return orderLineRefundedAmount + compensatedAmount
  }

  return (
    <Container>
      <Header onClick={() => setExpanded(!expanded)}>
        <div>
          {expanded ? <ChevronUp /> : <ChevronDown />}
          Order operations
        </div>
        {getOperationAmount() > 0 && !expanded && (
          <div>
            -
            <Money
              amount={getOperationAmount()}
              currencyUnit={order.currencyCode as DineroFactory.Currency}
            />
          </div>
        )}
      </Header>
      {expanded && (
        <>
          {orderLineDelivered && (
            <DeliveryInfo
              delivery={orderLineDelivered}
              orderLineId={orderLineId}
            />
          )}
          {orderLineCompensations.map((compensation) => (
            <Compensations
              key={compensation.id}
              compensation={compensation}
              orderLineId={orderLineId}
              currencyCode={order.currencyCode}
              salePriceAmount={salePriceAmount}
            />
          ))}
          {orderLineRefundedAmount > 0 &&
            orderLineRefund &&
            refund.getRefundsOnOrderLine(orderLineId)
              .map((refund) => (
                <RefundInfo
                  refund={refund}
                  currencyCode={order.currencyCode}
                  orderLineId={orderLineId}
                />
              ))}
          {orderLineReleased && (
            <ReleaseInfo
              release={orderLineReleased}
              currencyCode={order.currencyCode}
              orderLineId={orderLineId}
            />
          )}
          {orderCancelled && <CancellationInfo cancellation={orderCancelled} />}
        </>
      )}
    </Container>
  )
}

export default Operations
